import React from "react";
import { formatFloat } from "../../utils/format";
import { usePriceInverted, useOnchainAccountLendingPool } from "../../hooks/useOnchain";
import useGenericCall from "../../hooks/useGenericCall";
import { Changes } from "impermax-sdk";

const LIQ_K = 1.7;

interface LiquidationPriceProps {
  liquidationPrice: number;
  TWAPPrice: number;
  safetyMargin: number;
}

function LiquidationPrice({liquidationPrice, TWAPPrice, safetyMargin} : LiquidationPriceProps) {
  const safetyFactor = liquidationPrice > TWAPPrice ? liquidationPrice / TWAPPrice - 1 : TWAPPrice / liquidationPrice - 1;
  const riskFactor = safetyMargin - 1;
  const riskClass = 
    safetyFactor > riskFactor * LIQ_K ** 2 ? "risk-0" :
    safetyFactor > riskFactor * LIQ_K ** 1 ? "risk-1" :
    safetyFactor > riskFactor * LIQ_K ** 0 ? "risk-2" :
    safetyFactor > riskFactor * LIQ_K ** -1 ? "risk-3" :
    safetyFactor > riskFactor * LIQ_K ** -2 ? "risk-4" : "risk-5";
    
  return (
    <span className={"liquidation-price " + riskClass}>
      {formatFloat(liquidationPrice, 4)}
    </span>
  );
}

interface LiquidationPricesProps {
  changes?: Changes;
  overrideValues?: {
    valueCollateral: number;
    valueA: number;
    valueB: number;
  };
}

/**
 * Generates lending pool aggregate details.
 */
export default function LiquidationPrices({changes} : LiquidationPricesProps) {
  const priceInverted = usePriceInverted();
  const data = useGenericCall(useOnchainAccountLendingPool(), async onchainAccountLendingPool => {
    return {
      safetyMargin: await onchainAccountLendingPool.getSafetyMargin(),
      TWAPPrice: await onchainAccountLendingPool.getLendingPool().getTWAPPrice(),
      pricesDefault: await onchainAccountLendingPool.getLiquidationPrices(changes),
    }
  }, [changes, priceInverted]);

  if (!data) return (null);
  
  const [price0, price1] = data.pricesDefault;

  if (!price0 && !price1) return (<>-</>);
  if (price0 >= data.TWAPPrice || price1 <= data.TWAPPrice) return (
    <span className={"liquidation-price risk-5"}>Liquidatable</span>
  );

  return (<>
    <LiquidationPrice
      liquidationPrice={price0}
      TWAPPrice={data.TWAPPrice}
      safetyMargin={data.safetyMargin}
    /> - <LiquidationPrice
      liquidationPrice={price1}
      TWAPPrice={data.TWAPPrice}
      safetyMargin={data.safetyMargin}
    />
  </>);
}